import { inject, Injectable } from '@angular/core';
import { piIcon } from '@predictiveindex/pi-client-icons';
import { ReferenceProfileType } from '../constants-types';
import { TranslationService } from '../translations';

/**
 * Service for reference profile related operations.
 */
@Injectable({
    providedIn: 'root'
})
export class ReferenceProfileService {
    private readonly TR = inject(TranslationService);

    /**
     * Maps the reference profile id to the icon name.
     *
     * @param referenceProfileId The reference profile id.
     * @returns The associated icon when present, otherwise the default icon.
     */
    mapReferenceProfileIdToIconName(referenceProfileId?: number): piIcon {
        const iconMapping: Record<ReferenceProfileType, piIcon> = {
            [ReferenceProfileType.Adapter]: 'reference_profile_adapter',
            [ReferenceProfileType.Altruist]: 'reference_profile_altruist',
            [ReferenceProfileType.Analyzer]: 'reference_profile_analyzer',
            [ReferenceProfileType.Artisan]: 'reference_profile_artisan',
            [ReferenceProfileType.Captain]: 'reference_profile_captain',
            [ReferenceProfileType.Collaborator]:
                'reference_profile_collaborator',
            [ReferenceProfileType.Controller]: 'reference_profile_controller',
            [ReferenceProfileType.Guardian]: 'reference_profile_guardian',
            [ReferenceProfileType.Individualist]:
                'reference_profile_individualist',
            [ReferenceProfileType.Maverick]: 'reference_profile_maverick',
            [ReferenceProfileType.Operator]: 'reference_profile_operator',
            [ReferenceProfileType.Persuader]: 'reference_profile_persuader',
            [ReferenceProfileType.Promoter]: 'reference_profile_promoter',
            [ReferenceProfileType.Scholar]: 'reference_profile_scholar',
            [ReferenceProfileType.Specialist]: 'reference_profile_specialist',
            [ReferenceProfileType.Strategist]: 'reference_profile_strategist',
            [ReferenceProfileType.Venturer]: 'reference_profile_venturer'
        };

        return iconMapping[referenceProfileId] ?? 'hourglass_bold';
    }

    /**
     * Maps the reference profile id to the translated description.
     *
     * NOTE: The translated description returns html.
     *
     * @param referenceProfileId The reference profile id.
     * @returns The associated translated description when present, otherwise an empty string.
     */
    mapReferenceProfileIdToTranslatedDescription(
        referenceProfileId?: number
    ): string {
        const translationMapping: Record<ReferenceProfileType, string> = {
            [ReferenceProfileType.Adapter]:
                'toolkit-referenceprofile.tooltip-adapter-bolded',
            [ReferenceProfileType.Altruist]:
                'toolkit-referenceProfile.tooltip-altruist-bolded',
            [ReferenceProfileType.Analyzer]:
                'toolkit-referenceProfile.tooltip-analyzer-bolded',
            [ReferenceProfileType.Artisan]:
                'toolkit-referenceProfile.tooltip-craftsman-bolded',
            [ReferenceProfileType.Captain]:
                'toolkit-referenceProfile.tooltip-captain-bolded',
            [ReferenceProfileType.Collaborator]:
                'toolkit-referenceProfile.tooltip-collaborator-bolded',
            [ReferenceProfileType.Controller]:
                'toolkit-referenceProfile.tooltip-controller-bolded',
            [ReferenceProfileType.Guardian]:
                'toolkit-referenceProfile.tooltip-guardian-bolded',
            [ReferenceProfileType.Individualist]:
                'toolkit-referenceProfile.tooltip-individualist-bolded',
            [ReferenceProfileType.Maverick]:
                'toolkit-referenceProfile.tooltip-maverick-bolded',
            [ReferenceProfileType.Operator]:
                'toolkit-referenceProfile.tooltip-operator-bolded',
            [ReferenceProfileType.Persuader]:
                'toolkit-referenceProfile.tooltip-persuader-bolded',
            [ReferenceProfileType.Promoter]:
                'toolkit-referenceProfile.tooltip-promoter-bolded',
            [ReferenceProfileType.Scholar]:
                'toolkit-referenceProfile.tooltip-scholar-bolded',
            [ReferenceProfileType.Specialist]:
                'toolkit-referenceProfile.tooltip-specialist-bolded',
            [ReferenceProfileType.Strategist]:
                'toolkit-referenceProfile.tooltip-strategist-bolded',
            [ReferenceProfileType.Venturer]:
                'toolkit-referenceProfile.tooltip-venturer-bolded'
        };

        return this.TR.key(
            translationMapping[referenceProfileId] ??
                'label.common-terms.assessment-pending'
        );
    }
}
